import React, { useState, useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import hideBarIcon from '../../../../assets/images/hide-bar.svg';
import IntelSearchStorageService from '../../../../services/IntelSearchStorageService';
import ContextMenu from '../ContextMenu';
import PromptModal from '../PromptModal';
import { UiContext } from '../../../../context/UiContext';
import classes from './styles.module.scss';

export default function LeftPanel({
  isCollapsed,
  onToggle,
  onLoadSearch,
  currentUserId,
  refreshTrigger,
}) {
  const { t } = useTranslation();
  const { showModal } = useContext(UiContext);
  const [expandedSections, setExpandedSections] = useState({
    pinned: true,
    recent: true,
    projects: true,
    savedReports: true,
  });

  // Data state
  const [savedSearches, setSavedSearches] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [projects, setProjects] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);

  // UI state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedSearchId, setSelectedSearchId] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    items: [],
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptModalConfig, setPromptModalConfig] = useState({
    title: '',
    placeholder: '',
    onConfirm: () => {},
  });

  // Load data from localStorage
  const loadData = useCallback(() => {
    const searches = IntelSearchStorageService.getSavedSearches();
    const pinned = IntelSearchStorageService.getPinnedSearches();
    const projs = IntelSearchStorageService.getProjects();
    const recent = IntelSearchStorageService.getRecentSearches();

    setSavedSearches(searches);
    setPinnedIds(pinned);
    setProjects(projs);
    setRecentSearches(recent);
  }, []);

  // Initialize storage service with user ID
  useEffect(() => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      loadData();
    }
  }, [currentUserId, loadData]);

  // Reload data when refresh trigger changes
  useEffect(() => {
    if (currentUserId && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger, currentUserId, loadData]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // ==================== SEARCH ACTIONS ====================

  const handleLoadSearch = (search) => {
    if (onLoadSearch && currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      setSelectedSearchId(search.id);
      IntelSearchStorageService.updateLastUsed(search.id);
      // Pass search info along with payload so the parent can track the active search
      onLoadSearch(search.searchPayload, true, {
        id: search.id,
        name: search.name,
      });
    }
  };

  const handleRenameSearch = (searchId, newName) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.updateSearch(searchId, { name: newName });
      loadData();
    }
  };

  const handleDeleteSearch = (searchId) => {
    const search = savedSearches.find((s) => s.id === searchId);
    showModal({
      icon: 'delete',
      title: t('intelSearch.leftPanel.deleteConfirmation.title'),
      text: t('intelSearch.leftPanel.deleteConfirmation.text', {
        name: search?.name || t('intelSearch.leftPanel.unnamedSearch'),
      }),
      onConfirm: () => {
        if (currentUserId) {
          IntelSearchStorageService.init(currentUserId);
          IntelSearchStorageService.deleteSearch(searchId);
          if (selectedSearchId === searchId) {
            setSelectedSearchId(null);
          }
          loadData();
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.cancel'),
      confirmButtonLabel: t('common.delete'),
    });
  };

  const handleDuplicateSearch = (searchId) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.duplicateSearch(searchId);
      loadData();
    }
  };

  const handleTogglePin = (searchId) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.togglePin(searchId);
      loadData();
    }
  };

  const handleMoveToProject = (searchId, projectId) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.moveSearchToProject(searchId, projectId);
      loadData();
    }
  };

  const handleCopyLink = async (searchId) => {
    try {
      // Generate shareable link (could be enhanced with URL encoding)
      const link = `${window.location.origin}${window.location.pathname}?searchId=${searchId}`;
      await navigator.clipboard.writeText(link);
      // TODO: Show toast notification
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = `${window.location.origin}${window.location.pathname}?searchId=${searchId}`;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (fallbackError) {
        // Silently fail if clipboard copy fails
      }
      document.body.removeChild(textArea);
    }
  };

  // ==================== PROJECT ACTIONS ====================

  const handleCreateProject = () => {
    setPromptModalConfig({
      title: t('intelSearch.leftPanel.createProjectModal.title'),
      placeholder: t('intelSearch.leftPanel.createProjectModal.placeholder'),
      onConfirm: (name) => {
        // Ensure service is initialized before creating project
        if (currentUserId) {
          IntelSearchStorageService.init(currentUserId);
          IntelSearchStorageService.createProject(name);
          loadData();
        }
      },
    });
    setShowPromptModal(true);
  };

  const handleRenameProject = (projectId, newName) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.updateProject(projectId, { name: newName });
      loadData();
    }
  };

  const handleDeleteProject = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    const projectSearches = savedSearches.filter(
      (s) => s.projectId === projectId
    );

    showModal({
      icon: 'delete',
      title: t('intelSearch.leftPanel.deleteProjectConfirmation.title'),
      text: t('intelSearch.leftPanel.deleteProjectConfirmation.text', {
        name: project?.name || t('intelSearch.leftPanel.unnamedSearch'),
        count: projectSearches.length,
      }),
      onConfirm: () => {
        if (currentUserId) {
          // Delete project and searches
          IntelSearchStorageService.init(currentUserId);
          IntelSearchStorageService.deleteProject(projectId, true);
          loadData();
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.cancel'),
      confirmButtonLabel: t('common.delete'),
    });
  };

  const handleToggleProjectExpanded = (projectId) => {
    if (currentUserId) {
      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.toggleProjectExpanded(projectId);
      loadData();
    }
  };

  // ==================== EDITING ====================

  const handleEditCancel = () => {
    setEditingItemId(null);
    setEditValue('');
  };

  const handleEditStart = (item) => {
    setEditingItemId(item.id);
    setEditValue(item.name);
  };

  const handleEditSave = (itemId, isProject = false) => {
    if (!editValue.trim()) {
      handleEditCancel();
      return;
    }

    if (isProject) {
      handleRenameProject(itemId, editValue.trim());
    } else {
      handleRenameSearch(itemId, editValue.trim());
    }

    setEditingItemId(null);
    setEditValue('');
  };

  const handleEditKeyDown = (e, itemId, isProject = false) => {
    if (e.key === 'Enter') {
      handleEditSave(itemId, isProject);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // ==================== CONTEXT MENU ====================

  const showContextMenu = (e, search, type = 'saved') => {
    e.preventDefault();
    e.stopPropagation();

    const isPinned = pinnedIds.includes(search.id);
    const menuItems = [];

    if (type === 'saved' || type === 'pinned') {
      menuItems.push(
        {
          label: t('intelSearch.leftPanel.contextMenu.open'),
          onClick: () => handleLoadSearch(search),
        },
        {
          label: isPinned
            ? t('intelSearch.leftPanel.contextMenu.unpin')
            : t('intelSearch.leftPanel.contextMenu.pin'),
          onClick: () => handleTogglePin(search.id),
        },
        { separator: true },
        {
          label: t('intelSearch.leftPanel.contextMenu.rename'),
          onClick: () => handleEditStart(search),
        },
        {
          label: t('intelSearch.leftPanel.contextMenu.duplicate'),
          onClick: () => handleDuplicateSearch(search.id),
        },
        { separator: true }
      );

      // Add "Move to Project" submenu
      if (projects.length > 0) {
        projects.forEach((project) => {
          menuItems.push({
            label: `${t('intelSearch.leftPanel.contextMenu.moveTo')} ${
              project.name
            }`,
            onClick: () => handleMoveToProject(search.id, project.id),
          });
        });
        menuItems.push({ separator: true });
      }

      menuItems.push(
        {
          label: t('intelSearch.leftPanel.contextMenu.copyLink'),
          onClick: () => handleCopyLink(search.id),
        },
        {
          label: t('intelSearch.leftPanel.contextMenu.delete'),
          onClick: () => handleDeleteSearch(search.id),
        }
      );
    } else if (type === 'project') {
      menuItems.push(
        {
          label: t('intelSearch.leftPanel.contextMenu.rename'),
          onClick: () => handleEditStart(search, true),
        },
        {
          label: t('intelSearch.leftPanel.contextMenu.delete'),
          onClick: () => handleDeleteProject(search.id),
        }
      );
    }

    setContextMenu({
      isVisible: true,
      x: e.clientX,
      y: e.clientY,
      items: menuItems,
    });
  };

  // ==================== DRAG AND DROP ====================

  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.index === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    if (currentUserId) {
      // Reorder pinned items
      const newOrder = [...pinnedIds];
      const [removed] = newOrder.splice(draggedItem.index, 1);
      newOrder.splice(dropIndex, 0, removed);

      IntelSearchStorageService.init(currentUserId);
      IntelSearchStorageService.reorderPinned(newOrder);
      loadData();
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // ==================== RENDER HELPERS ====================

  const renderSearchItem = (search, type = 'saved', index = null) => {
    const isEditing = editingItemId === search.id;
    const isDraggable = type === 'pinned';
    const isDragging = draggedItem?.item.id === search.id;
    const isDragOver = dragOverIndex === index;
    const isSelected = selectedSearchId === search.id;

    return (
      <li
        key={search.id}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */
        role="button"
        tabIndex={0}
        className={classNames(classes.reportItem, {
          [classes.dragging]: isDragging,
          [classes.dragOver]: isDragOver,
          [classes.selected]: isSelected,
        })}
        draggable={isDraggable && !isEditing}
        onDragStart={
          isDraggable ? (e) => handleDragStart(e, search, index) : undefined
        }
        onDragOver={isDraggable ? (e) => handleDragOver(e, index) : undefined}
        onDragLeave={isDraggable ? handleDragLeave : undefined}
        onDrop={isDraggable ? (e) => handleDrop(e, index) : undefined}
        onDragEnd={isDraggable ? handleDragEnd : undefined}
        onClick={() => {
          if (!isEditing) {
            handleLoadSearch(search);
          }
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isEditing) {
            e.preventDefault();
            handleLoadSearch(search);
          }
        }}
        onContextMenu={(e) => showContextMenu(e, search, type)}
      >
        {isEditing ? (
          <div className={classes.editMode}>
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleEditKeyDown(e, search.id, false)}
              className={classes.editInput}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEditSave(search.id, false);
              }}
              className={classes.saveButton}
            >
              ✓
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEditCancel();
              }}
              className={classes.cancelButton}
            >
              ✕
            </button>
          </div>
        ) : (
          <>
            {isDraggable && <span className={classes.dragHandle}>⋮⋮</span>}
            <div className={classes.itemContent}>
              <span className={classes.reportName} title={search.name}>
                {search.name}
              </span>
              {search.description && (
                <span
                  className={classes.description}
                  title={search.description}
                >
                  {search.description}
                </span>
              )}
            </div>
          </>
        )}
      </li>
    );
  };

  const renderRecentItem = (recent) => {
    // Generate a display name from the search payload
    const generateName = (payload) => {
      const parts = [];
      if (payload.searchChips?.length > 0) {
        const chipNames = payload.searchChips
          .slice(0, 2)
          .map((chip) => chip.value || chip)
          .join(', ');
        parts.push(chipNames);
      }
      if (payload.filters?.country?.length > 0) {
        parts.push(`${payload.filters.country.length} countries`);
      }
      return parts.length > 0
        ? parts.join(' • ')
        : t('intelSearch.leftPanel.unnamedSearch');
    };

    const displayName = generateName(recent.searchPayload);

    return (
      <li
        key={recent.id}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */
        role="button"
        tabIndex={0}
        className={classes.reportItem}
        onClick={() => {
          // Clear selected saved search when recent search is clicked
          setSelectedSearchId(null);
          // For recent items, we only have payload, not a full search object
          if (onLoadSearch) {
            onLoadSearch(recent.searchPayload);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Clear selected saved search when recent search is selected
            setSelectedSearchId(null);
            if (onLoadSearch) {
              onLoadSearch(recent.searchPayload);
            }
          }
        }}
      >
        <div className={classes.itemContent}>
          <span className={classes.reportName} title={displayName}>
            {displayName}
          </span>
          <span className={classes.timeLabel}>
            {moment(recent.timestamp).fromNow()}
          </span>
        </div>
      </li>
    );
  };

  const renderProjectItem = (project) => {
    const isEditing = editingItemId === project.id;
    const projectSearches = savedSearches.filter(
      (s) => s.projectId === project.id
    );

    return (
      <div key={project.id} className={classes.projectItem}>
        <div
          className={classes.projectHeader}
          onClick={() => !isEditing && handleToggleProjectExpanded(project.id)}
          onContextMenu={(e) => showContextMenu(e, project, 'project')}
        >
          <div
            className={classNames(classes.arrow, {
              [classes.collapsed]: !project.isExpanded,
            })}
          />
          {isEditing ? (
            <div className={classes.editMode}>
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => handleEditKeyDown(e, project.id, true)}
                className={classes.editInput}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditSave(project.id, true);
                }}
                className={classes.saveButton}
              >
                ✓
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCancel();
                }}
                className={classes.cancelButton}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <span className={classes.projectName} title={project.name}>
                {project.name}
              </span>
              <span className={classes.countBadge}>
                {projectSearches.length}
              </span>
            </>
          )}
        </div>
        {project.isExpanded && projectSearches.length > 0 && (
          <ul className={classes.projectSearches}>
            {projectSearches.map((search) => renderSearchItem(search, 'saved'))}
          </ul>
        )}
      </div>
    );
  };

  const renderSection = (title, sectionKey, emptyMessage) => {
    let items = [];
    let renderFunction = null;

    switch (sectionKey) {
      case 'pinned':
        items = pinnedIds
          .map((id) => savedSearches.find((s) => s.id === id))
          .filter(Boolean);
        renderFunction = (item, index) =>
          renderSearchItem(item, 'pinned', index);
        break;
      case 'recent':
        items = recentSearches;
        renderFunction = renderRecentItem;
        break;
      case 'projects':
        items = projects;
        renderFunction = renderProjectItem;
        break;
      case 'savedReports':
        items = savedSearches.filter((s) => !s.projectId);
        renderFunction = (item) => renderSearchItem(item, 'saved');
        break;
      default:
        break;
    }

    return (
      <div className={classes.section}>
        <div
          className={classes.sectionHeader}
          onClick={() => toggleSection(sectionKey)}
        >
          <span className={classes.sectionTitle}>{title}</span>
          {sectionKey === 'projects' && (
            <button
              type="button"
              className={classes.addButton}
              onClick={(e) => {
                e.stopPropagation();
                handleCreateProject();
              }}
              title={t('intelSearch.leftPanel.createProject')}
            >
              +
            </button>
          )}
          <div
            className={classNames(classes.arrow, {
              [classes.collapsed]: !expandedSections[sectionKey],
            })}
          />
        </div>
        {expandedSections[sectionKey] && (
          <div className={classes.sectionContent}>
            {items.length === 0 && (
              <div className={classes.emptyState}>{emptyMessage}</div>
            )}
            {items.length > 0 && sectionKey === 'projects' && (
              <div className={classes.projectsList}>
                {items.map(renderFunction)}
              </div>
            )}
            {items.length > 0 && sectionKey !== 'projects' && (
              <ul className={classes.reportList}>
                {items.map(renderFunction)}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  // Memoized callbacks for child components
  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ isVisible: false, x: 0, y: 0, items: [] });
  }, []);

  const handlePromptModalClose = useCallback(() => {
    setShowPromptModal(false);
  }, []);

  const handlePromptModalConfirm = useCallback(
    (value) => {
      promptModalConfig.onConfirm(value);
    },
    [promptModalConfig]
  );

  if (isCollapsed) {
    return null;
  }

  return (
    <>
      <div className={classes.LeftPanel}>
        <div className={classes.header}>
          <h2 className={classes.title}>{t('intelSearch.leftPanel.title')}</h2>
          <button
            type="button"
            className={classes.toggleButton}
            onClick={onToggle}
            title={t('intelSearch.leftPanel.collapse')}
          >
            <img src={hideBarIcon} alt="Toggle panel" />
          </button>
        </div>

        {renderSection(
          t('intelSearch.leftPanel.sections.pinned'),
          'pinned',
          t('intelSearch.leftPanel.emptyStates.pinned')
        )}
        {renderSection(
          t('intelSearch.leftPanel.sections.recent'),
          'recent',
          t('intelSearch.leftPanel.emptyStates.recent')
        )}
        {renderSection(
          t('intelSearch.leftPanel.sections.projects'),
          'projects',
          t('intelSearch.leftPanel.emptyStates.projects')
        )}
        {renderSection(
          t('intelSearch.leftPanel.sections.savedReports'),
          'savedReports',
          t('intelSearch.leftPanel.emptyStates.savedReports')
        )}
      </div>

      {/* Context Menu */}
      <ContextMenu
        isVisible={contextMenu.isVisible}
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenu.items}
        onClose={handleContextMenuClose}
      />

      {/* Prompt Modal */}
      <PromptModal
        show={showPromptModal}
        handleClose={handlePromptModalClose}
        onConfirm={handlePromptModalConfirm}
        title={promptModalConfig.title}
        placeholder={promptModalConfig.placeholder}
      />
    </>
  );
}
